import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { TrackingContext } from '../../TrackingContext';
import { PageCardUIContext } from '../../PageCardUIContext';
import BaseGuidanceCard from '../BaseGuidanceCard';
import {
  useCreatePageMutation,
  Wws_AllowedCreatePageType,
  Wws_PageAttributes,
  PagesFragment,
  Wws_Page,
  PagesQuery,
} from '../../generated/graphql';
import { pagesQuery } from '../LeftContainer/graphql';
import Honeybadger from 'honeybadger-js';

interface EmptyDressCodeCardProps {
  title?: string;
  description?: string;
  buttonText?: string;
  onFirstSeen?: () => void;
  onCreatedPage?: (page: PagesFragment | Wws_Page) => void;
  testId?: string;
}

const EmptyDressCodeCard: React.FC<EmptyDressCodeCardProps> = ({
  title = 'Dress Code',
  description = "Guide your guests' attire picks by adding your dress code.",
  buttonText = 'Add dress code',
  onFirstSeen,
  onCreatedPage,
  testId = 'dress-code-guidance-card',
}) => {
  const { track, dashboardImpressionFired } = useContext(TrackingContext);
  const { setPageOpen } = useContext(PageCardUIContext);
  const [createPage, { loading: isCreating }] = useCreatePageMutation({
    update: (cache, { data }) => {
      if (!data?.createPage) return;
      try {
        const { pages } = cache.readQuery<PagesQuery>({ query: pagesQuery })!;
        cache.writeQuery({
          query: pagesQuery,
          data: { pages: [...pages, data.createPage] },
        });

        if (data?.createPage.id && setPageOpen) {
          // Register the page with PageCardUIContext so it gets the dropdown caret but keep it closed
          setPageOpen(data.createPage.id, false);
        }
      } catch (err) {
        Honeybadger.notify(err, {
          context: { component: 'EmptyDressCodeCard', action: 'apollo-cache-update' },
        });
      }
    },
  });

  const handleCreateDressCodePage = useCallback(async () => {
    // Prevent multiple submissions if already creating
    if (isCreating) return;

    track('WWS Dashboard Interaction', {
      action: 'add',
      actionName: 'dress code page',
      source: 'guidance card',
    });

    try {
      const attrs: Wws_PageAttributes = { title, show: true, type: Wws_AllowedCreatePageType.DressCodePage };
      const result = await createPage({ variables: { attributes: attrs } });
      const createdPage = result.data?.createPage;

      track('WWS Page Added', {
        action: 'add',
        pageTitle: createdPage?.title || title,
        pageVisible: createdPage?.show ?? true,
        pageType: createdPage?.type ?? Wws_AllowedCreatePageType.DressCodePage,
        page: createdPage?.title || title,
      });

      if (createdPage && onCreatedPage) {
        onCreatedPage(createdPage as PagesFragment & Wws_Page);
      }

      // No full-page reload here — callers can handle navigation/edit-in-preview flows via onCreatedPage
    } catch (error) {
      Honeybadger.notify(error, {
        message: 'Error creating dress code page from guidance card',
        context: { error },
      });
    }
  }, [createPage, title, track, onCreatedPage, isCreating]);

  const firstSeenRef = useRef(false);
  useEffect(() => {
    if (firstSeenRef.current || dashboardImpressionFired) return;
    firstSeenRef.current = true;

    track('WWS Dashboard Impression', {
      type: 'dress code guidance card',
    });
    if (onFirstSeen) onFirstSeen();
  }, [track, onFirstSeen, dashboardImpressionFired]);

  return (
    <div data-testid={testId}>
      <BaseGuidanceCard
        bulletPoints={[]}
        titleText={title}
        description={description}
        primaryLinkText={buttonText}
        primaryOnClick={handleCreateDressCodePage}
        isLoading={isCreating}
        type="dresscode"
      />
    </div>
  );
};

export default EmptyDressCodeCard;
